import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from "react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2,
  Landmark,
  User as UserIcon,
  Package,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
  Inbox,
  Columns3,
  TrendingUp,
  Trophy,
  Banknote,
  Hash,
  Layers,
  ChevronRight,
  ChevronDown,
  Check,
} from "lucide-react";
import { useFillViewportHeight } from "@/hooks/useFillViewportHeight";
import {
  useResizableColumns,
  type ResizableColumnDef,
} from "@/hooks/useResizableColumns";
import { ColumnResizeHandle } from "@/components/ui/column-resize-handle";
import { CellText } from "@/components/ui/cell-text";
import {
  useDealFieldEdit,
  type DealField,
  type StageLite,
} from "@/hooks/useDealFieldEdit";
import InlineEditCurrency from "@/components/inline-edit/InlineEditCurrency";
import InlineEditDate from "@/components/inline-edit/InlineEditDate";
import InlineEditSelect from "@/components/inline-edit/InlineEditSelect";
import InlineEditUser from "@/components/inline-edit/InlineEditUser";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const TYPE_CFG: Record<
  string,
  { short: string; icon: any; color: string; bg: string; ring: string }
> = {
  public_admin: {
    short: "B2G",
    icon: Landmark,
    color: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    ring: "ring-amber-200 dark:ring-amber-800",
  },
  empresa: {
    short: "B2B",
    icon: Building2,
    color: "text-sky-700 dark:text-sky-300",
    bg: "bg-sky-50 dark:bg-sky-950/40",
    ring: "ring-sky-200 dark:ring-sky-800",
  },
  cliente_final: {
    short: "B2C",
    icon: UserIcon,
    color: "text-emerald-700 dark:text-emerald-300",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    ring: "ring-emerald-200 dark:ring-emerald-800",
  },
};

type SortKey =
  | "title"
  | "value"
  | "stage"
  | "status"
  | "contact"
  | "owner"
  | "updatedAt"
  | "expectedCloseDate"
  | "itemsCount";

type SortDir = "asc" | "desc";

export type DealsTableRow = {
  deal: {
    id: number;
    title: string;
    value: string;
    status: string;
    stageId: number;
    // Necessário para a célula de vendedor editável saber o valor atual —
    // `owner` só traz o nome, e a mutation precisa do id.
    ownerUserId: number | null;
    expectedCloseDate: Date | string | null;
    updatedAt: Date | string;
    createdAt: Date | string;
  };
  contact: {
    id: number | null;
    name: string | null;
    lastName: string | null;
    companyName: string | null;
    contactType: string | null;
    city: string | null;
    state: string | null;
  } | null;
  owner: { id: number | null; name: string | null } | null;
  stage: {
    id: number;
    name: string;
    color: string;
    probability: number;
    isWon: boolean;
    isLost: boolean;
  } | null;
  products?: Array<{
    productId: number | null;
    name: string | null;
    quantity: number;
  }>;
};

// ─── Config de colunas ──────────────────────────────────────────────────────
type ColumnKey =
  | "title"
  | "contact"
  | "stage"
  | "value"
  | "products"
  | "itemsCount"
  | "owner"
  | "expectedCloseDate"
  | "updatedAt";

/**
 * Editor de cada coluna. Colunas sem `editor` continuam só de leitura.
 *
 * "Oportunidade" fica fora de propósito: a célula é um link para a
 * negociação, e um clique não pode significar duas coisas.
 */
type EditorDescriptor =
  | { kind: "currency"; field: "value" }
  | { kind: "date"; field: "expectedCloseDate" }
  | { kind: "stage"; field: "stageId" }
  | { kind: "owner"; field: "ownerUserId" };

/**
 * `width` é largura de verdade, não piso: a tabela roda em `table-layout:
 * fixed`. Antes só havia `minWidth` e, com layout automático, cada coluna
 * crescia até o conteúdo — um contato com nome e órgão longos esticava a
 * coluna muito além dos 180px pedidos e jogava o resto da tabela para fora da
 * tela do notebook. Com largura fixa os `truncate` das células finalmente
 * valem, e quem precisa de mais espaço arrasta a borda do cabeçalho.
 *
 * A soma dos padrões (1025px) foi escolhida para caber num notebook de 1366px
 * com a sidebar aberta.
 */
type ColumnMeta = {
  label: string;
  sortable: boolean;
  sortKey?: SortKey;
  /** Largura padrão em px — ponto de partida do arraste. */
  width: number;
  /** Piso do arraste em px. */
  minWidth: number;
  align?: "left" | "right";
  editor?: EditorDescriptor;
  /** Congelada à esquerda durante a rolagem horizontal. */
  pinned?: boolean;
};

const COLUMN_META: Record<ColumnKey, ColumnMeta> = {
  title: {
    label: "Oportunidade",
    sortable: true,
    sortKey: "title",
    width: 190,
    minWidth: 130,
    pinned: true,
  },
  contact: {
    label: "Contato",
    sortable: true,
    sortKey: "contact",
    width: 175,
    minWidth: 120,
  },
  stage: {
    label: "Estágio",
    sortable: true,
    sortKey: "stage",
    width: 120,
    minWidth: 90,
    editor: { kind: "stage", field: "stageId" },
  },
  value: {
    label: "Valor",
    sortable: true,
    sortKey: "value",
    width: 100,
    minWidth: 80,
    align: "right",
    editor: { kind: "currency", field: "value" },
  },
  products: { label: "Produtos", sortable: false, width: 120, minWidth: 80 },
  itemsCount: {
    label: "Itens",
    sortable: true,
    sortKey: "itemsCount",
    width: 50,
    minWidth: 44,
    align: "right",
  },
  owner: {
    label: "Vendedor",
    sortable: true,
    sortKey: "owner",
    width: 115,
    minWidth: 80,
    editor: { kind: "owner", field: "ownerUserId" },
  },
  expectedCloseDate: {
    label: "Previsão",
    sortable: true,
    sortKey: "expectedCloseDate",
    width: 80,
    minWidth: 70,
    editor: { kind: "date", field: "expectedCloseDate" },
  },
  updatedAt: {
    label: "Atualizada",
    sortable: true,
    sortKey: "updatedAt",
    width: 75,
    minWidth: 64,
  },
};

// ─── Edição inline ──────────────────────────────────────────────────────────

/** Campos reais do deal que o overlay otimista sobrescreve numa linha. */
type DealPatch = Record<string, unknown>;

/**
 * O que a tela precisa exibir na hora mas não é campo do deal.
 *
 * Fica separado do `DealPatch` de propósito: o nome do vendedor escolhido não
 * existe em `deals` — a mutation manda só o id — e enfiá-lo no objeto de
 * domínio significaria um campo fantasma circulando junto com os reais.
 */
type DealDisplayPatch = { ownerName?: string | null };

type OverlayEntry = { deal: DealPatch; display: DealDisplayPatch };

type DealsEditCtxValue = {
  canEditField: (f: DealField) => boolean;
  commit: (
    dealId: number,
    field: DealField,
    next: unknown,
    ctx?: { dealTitle?: string; optimistic?: Record<string, unknown> },
  ) => Promise<void>;
  stages: StageLite[];
};

// Contexto em vez de props: GroupBlock fica entre DealsTable e DealRow e teria
// que repassar tudo só para atravessar.
const DealsEditCtx = createContext<DealsEditCtxValue | null>(null);

/**
 * Aplica o patch otimista numa linha, recalculando também os objetos derivados
 * que a célula exibe (`stage`, `owner`) — senão a linha mostraria o estágio
 * antigo mesmo com o `stageId` novo.
 */
function patchRow(
  row: DealsTableRow,
  entry: OverlayEntry | undefined,
  stages: StageLite[],
): DealsTableRow {
  if (!entry) return row;
  const { deal: patch, display } = entry;
  const next: any = { ...row, deal: { ...row.deal, ...patch } };

  if ("stageId" in patch) {
    const s = stages.find((x) => x.id === patch.stageId);
    if (s) {
      next.stage = {
        id: s.id,
        name: s.name,
        color: s.color ?? "#94a3b8",
        probability: s.probability ?? 0,
        isWon: !!s.isWon,
        isLost: !!s.isLost,
      };
      // O status acompanha o estágio: sem isso a linha continuaria pintada
      // como "em aberto" logo após ser marcada como ganha.
      next.deal.status = s.isWon ? "won" : s.isLost ? "lost" : "open";
    }
  }
  if ("ownerUserId" in patch) {
    next.owner =
      patch.ownerUserId == null
        ? null
        : {
            id: patch.ownerUserId,
            name: display.ownerName ?? row.owner?.name ?? "",
          };
  }
  return next;
}

const DEFAULT_COLUMNS: ColumnKey[] = [
  "title",
  "contact",
  "stage",
  "value",
  "products",
  "itemsCount",
  "owner",
  "expectedCloseDate",
  "updatedAt",
];

const STORAGE_KEY = "crm-deals-table-columns";
const WIDTH_STORAGE_KEY = "crm-deals-table-widths";

const COLUMN_SIZE_DEFS: ResizableColumnDef<ColumnKey>[] = DEFAULT_COLUMNS.map(
  (key) => ({
    key,
    width: COLUMN_META[key].width,
    minWidth: COLUMN_META[key].minWidth,
  }),
);

// ─── Agrupamento ────────────────────────────────────────────────────────────
type GroupKey = "none" | "stage" | "owner" | "organization" | "contactType" | "status";

const GROUP_LABEL: Record<GroupKey, string> = {
  none: "Sem agrupamento",
  stage: "Estágio",
  owner: "Vendedor",
  organization: "Organização",
  contactType: "Tipo (B2G/B2B/B2C)",
  status: "Status (aberto/ganho/perdido)",
};

const GROUP_STORAGE_KEY = "crm-deals-table-group";

function useGroupPref() {
  const [groupBy, setGroupBy] = useState<GroupKey>(() => {
    if (typeof window === "undefined") return "none";
    try {
      const raw = window.localStorage.getItem(GROUP_STORAGE_KEY);
      if (raw && (raw in GROUP_LABEL)) return raw as GroupKey;
    } catch {
      /* fallback */
    }
    return "none";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(GROUP_STORAGE_KEY, groupBy);
    }
  }, [groupBy]);
  return { groupBy, setGroupBy };
}

const STATUS_LABEL: Record<string, string> = {
  open: "Em aberto",
  won: "Fechadas",
  lost: "Perdidas",
};

function groupRows(
  rows: Array<DealsTableRow & { itemsCount: number }>,
  key: GroupKey,
): Array<{
  groupKey: string;
  label: string;
  color?: string;
  rows: Array<DealsTableRow & { itemsCount: number }>;
}> {
  if (key === "none") {
    return [{ groupKey: "__all", label: "", rows }];
  }
  const map = new Map<
    string,
    {
      groupKey: string;
      label: string;
      color?: string;
      rows: Array<DealsTableRow & { itemsCount: number }>;
    }
  >();
  for (const r of rows) {
    let gKey = "—";
    let label = "—";
    let color: string | undefined;
    switch (key) {
      case "stage":
        gKey = r.stage ? `s:${r.stage.id}` : "s:none";
        label = r.stage?.name ?? "Sem estágio";
        color = r.stage?.color;
        break;
      case "owner":
        gKey = r.owner?.id != null ? `u:${r.owner.id}` : "u:none";
        label = r.owner?.name ?? "Sem vendedor";
        break;
      case "organization": {
        const rawCompany = r.contact?.companyName ?? null;
        const looksLikeCipher =
          rawCompany != null &&
          rawCompany.length > 40 &&
          /^[a-f0-9]+:[a-f0-9]+:[a-f0-9]+/.test(rawCompany);
        const company = looksLikeCipher ? null : rawCompany;
        gKey = company ? `o:${company.toLocaleLowerCase()}` : "o:none";
        label = company ?? "Sem organização";
        break;
      }
      case "contactType":
        gKey = `t:${r.contact?.contactType ?? "none"}`;
        label =
          r.contact?.contactType === "public_admin"
            ? "B2G · Órgão público"
            : r.contact?.contactType === "empresa"
            ? "B2B · Empresa"
            : r.contact?.contactType === "cliente_final"
            ? "B2C · Pessoa"
            : "Sem tipo";
        break;
      case "status":
        gKey = `st:${r.deal.status}`;
        label = STATUS_LABEL[r.deal.status] ?? r.deal.status;
        break;
    }
    let entry = map.get(gKey);
    if (!entry) {
      entry = { groupKey: gKey, label, color, rows: [] };
      map.set(gKey, entry);
    }
    entry.rows.push(r);
  }
  // Ordena os grupos: stage por posição do estágio, status com ordem fixa, demais alfabético
  const arr = Array.from(map.values());
  if (key === "status") {
    const order = ["st:open", "st:won", "st:lost"];
    arr.sort((a, b) => order.indexOf(a.groupKey) - order.indexOf(b.groupKey));
  } else if (key === "stage") {
    // Usa a ordem em que apareceram (já vem ordenada do backend/filtro)
    // mantém como está
  } else {
    arr.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }
  return arr;
}

function summarizeGroup(
  rows: Array<DealsTableRow & { itemsCount: number }>,
): {
  count: number;
  totalValue: number;
  totalItems: number;
  wonCount: number;
  wonValue: number;
  avgTicketDeal: number;
  avgTicketItem: number;
} {
  let totalValue = 0;
  let totalItems = 0;
  let wonCount = 0;
  let wonValue = 0;
  for (const r of rows) {
    const v = Number(r.deal.value ?? 0);
    totalValue += v;
    totalItems += r.itemsCount;
    if (r.deal.status === "won") {
      wonCount++;
      wonValue += v;
    }
  }
  return {
    count: rows.length,
    totalValue,
    totalItems,
    wonCount,
    wonValue,
    avgTicketDeal: rows.length > 0 ? totalValue / rows.length : 0,
    avgTicketItem: totalItems > 0 ? totalValue / totalItems : 0,
  };
}

function useColumnPrefs() {
  const [visible, setVisible] = useState<Set<ColumnKey>>(() => {
    if (typeof window === "undefined") return new Set(DEFAULT_COLUMNS);
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        const set = new Set<ColumnKey>(
          arr.filter((k): k is ColumnKey => k in COLUMN_META),
        );
        if (set.size === 0) return new Set(DEFAULT_COLUMNS);
        return set;
      }
    } catch {
      /* fallback */
    }
    return new Set(DEFAULT_COLUMNS);
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visible)),
      );
    }
  }, [visible]);
  function toggle(key: ColumnKey) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Não deixa esconder todas; mantém pelo menos "title".
        if (next.size === 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }
  function reset() {
    setVisible(new Set(DEFAULT_COLUMNS));
  }
  return { visible, toggle, reset };
}

// ─── Componente principal ───────────────────────────────────────────────────
export default function DealsTable({
  rows,
  isLoading,
  stages = [],
}: {
  rows: DealsTableRow[];
  isLoading?: boolean;
  /** Estágios do funil ativo — alimenta a célula de estágio editável. */
  stages?: StageLite[];
}) {
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const { visible, toggle, reset } = useColumnPrefs();
  const { groupBy, setGroupBy } = useGroupPref();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const { widths, startResize, resetWidth, resetWidths, resizingKey } =
    useResizableColumns(COLUMN_SIZE_DEFS, WIDTH_STORAGE_KEY);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Medido no quadro inteiro (rolagem + rodapé), não só no scroller: com o
  // rodapé de métricas empilhado por fora, ele e a barra de rolagem horizontal
  // acabavam abaixo da dobra, e a pessoa tinha que rolar a página inteira para
  // conseguir mover a tabela para o lado.
  const frameRef = useRef<HTMLDivElement>(null);
  const frameHeight = useFillViewportHeight(frameRef, {
    gutter: 24,
    min: 320,
    // O quadro só existe depois do skeleton; sem isto o efeito mediria um ref
    // vazio e nunca mais rodaria.
    enabled: !isLoading && rows.length > 0,
  });
  // Sombra da coluna fixa só quando existe conteúdo escondido à esquerda.
  const [scrolledX, setScrolledX] = useState(false);

  // Roda do mouse rola para o lado quando não há mais o que rolar para baixo
  // (ou com Shift). Sem isso, a única forma de alcançar as colunas escondidas
  // é arrastar a barra de rolagem — trackpad com gesto lateral é minoria.
  // Listener nativo porque o `onWheel` do React é passivo e ignora o
  // `preventDefault`.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0 || e.deltaX !== 0) return;
      if (el.scrollWidth - el.clientWidth <= 1) return;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      if (e.shiftKey || (e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // Mesmo motivo do `enabled` acima: o scroller só existe depois do skeleton.
  }, [isLoading, rows.length]);

  // ── Edição inline ───────────────────────────────────────────────────────
  // O valor editado é aplicado localmente ANTES dos memos de ordenação,
  // agrupamento e totais. Corrigir o cache de `crm.deals.list` não serviria:
  // ele é chaveado pelos filtros ativos, e mesmo assim os agregados desta
  // tabela (que são calculados aqui) continuariam mostrando o número antigo
  // até a query voltar.
  const [overlay, setOverlay] = useState<Map<number, OverlayEntry>>(new Map());

  const applyOverlay = useCallback(
    (
      dealId: number,
      patch: Record<string, unknown>,
      display?: Record<string, unknown>,
    ) => {
      setOverlay((prev) => {
        const next = new Map(prev);
        const atual = next.get(dealId);
        next.set(dealId, {
          deal: { ...(atual?.deal ?? {}), ...patch },
          display: { ...(atual?.display ?? {}), ...(display ?? {}) },
        });
        return next;
      });
    },
    [],
  );

  const clearOverlay = useCallback((dealId: number) => {
    setOverlay((prev) => {
      if (!prev.has(dealId)) return prev;
      const next = new Map(prev);
      next.delete(dealId);
      return next;
    });
  }, []);

  const fieldEdit = useDealFieldEdit({
    stages,
    onOptimistic: applyOverlay,
    onRollback: clearOverlay,
    // Em onSettled os dados já foram invalidados: manter o overlay depois
    // disso deixaria o valor otimista grudado por cima do servidor.
    onSettled: clearOverlay,
  });

  const effectiveRows = useMemo(
    () => (overlay.size === 0 ? rows : rows.map((r) => patchRow(r, overlay.get(r.deal.id), stages))),
    [rows, overlay, stages],
  );

  // Calcula quantidade de itens por deal
  const rowsWithItems = useMemo(
    () =>
      effectiveRows.map((r) => ({
        ...r,
        itemsCount: (r.products ?? []).reduce(
          (acc, p) => acc + Number(p.quantity ?? 0),
          0,
        ),
      })),
    [effectiveRows],
  );

  const sorted = useMemo(() => {
    const arr = [...rowsWithItems];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const get = (row: (typeof rowsWithItems)[number]): string | number => {
        switch (sortKey) {
          case "title":
            return row.deal.title.toLocaleLowerCase();
          case "value":
            return Number(row.deal.value ?? 0);
          case "stage":
            return row.stage?.name?.toLocaleLowerCase() ?? "";
          case "status":
            return row.deal.status;
          case "contact": {
            const full = [row.contact?.name, row.contact?.lastName]
              .filter(Boolean)
              .join(" ")
              .toLocaleLowerCase();
            return full;
          }
          case "owner":
            return (row.owner?.name ?? "").toLocaleLowerCase();
          case "updatedAt":
            return new Date(row.deal.updatedAt).getTime();
          case "expectedCloseDate":
            return row.deal.expectedCloseDate
              ? new Date(row.deal.expectedCloseDate).getTime()
              : 0;
          case "itemsCount":
            return row.itemsCount;
        }
      };
      const va = get(a);
      const vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [rowsWithItems, sortKey, sortDir]);

  // ── Totais para o rodapé (baseado nas linhas filtradas) ──────────────────
  const totals = useMemo(() => {
    let totalValue = 0;
    let openValue = 0;
    let wonValue = 0;
    let lostValue = 0;
    let openCount = 0;
    let wonCount = 0;
    let lostCount = 0;
    let totalItems = 0;
    let wonItems = 0;
    for (const r of sorted) {
      const v = Number(r.deal.value ?? 0);
      totalValue += v;
      totalItems += r.itemsCount;
      if (r.deal.status === "open") {
        openValue += v;
        openCount++;
      } else if (r.deal.status === "won") {
        wonValue += v;
        wonCount++;
        wonItems += r.itemsCount;
      } else if (r.deal.status === "lost") {
        lostValue += v;
        lostCount++;
      }
    }
    const avgTicketDeal = sorted.length > 0 ? totalValue / sorted.length : 0;
    const avgTicketItem = totalItems > 0 ? totalValue / totalItems : 0;
    const avgTicketWonDeal = wonCount > 0 ? wonValue / wonCount : 0;
    const avgTicketWonItem = wonItems > 0 ? wonValue / wonItems : 0;
    return {
      totalValue,
      openValue,
      wonValue,
      lostValue,
      openCount,
      wonCount,
      lostCount,
      totalItems,
      wonItems,
      avgTicketDeal,
      avgTicketItem,
      avgTicketWonDeal,
      avgTicketWonItem,
      count: sorted.length,
    };
  }, [sorted]);

  const groups = useMemo(() => groupRows(sorted, groupBy), [sorted, groupBy]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(
        key === "value" || key === "updatedAt" || key === "itemsCount"
          ? "desc"
          : "asc",
      );
    }
  }

  if (isLoading) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="p-3 border-b bg-muted/30">
          <Skeleton className="h-4 w-40" />
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-3 border-b last:border-b-0">
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="border rounded-lg py-16 text-center">
        <Inbox className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium">Nenhuma oportunidade encontrada</p>
        <p className="text-xs text-muted-foreground mt-1">
          Ajuste os filtros ou crie uma nova oportunidade.
        </p>
      </div>
    );
  }

  const visibleCols = DEFAULT_COLUMNS.filter((c) => visible.has(c));
  const totalWidth = visibleCols.reduce((sum, c) => sum + widths[c], 0);

  function toggleGroup(gKey: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(gKey)) next.delete(gKey);
      else next.add(gKey);
      return next;
    });
  }

  return (
    <DealsEditCtx.Provider
      value={{
        canEditField: fieldEdit.canEditField,
        commit: fieldEdit.commit,
        stages,
      }}
    >
    <div className="space-y-3">
      {/* Diálogo de motivo da perda, disparado ao mover uma linha para um
          estágio de perda direto na célula de estágio. */}
      {fieldEdit.lossDialog}
      {/* Toolbar da tabela */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {totals.count} oportunidade{totals.count === 1 ? "" : "s"}
          {totals.totalItems > 0 && (
            <span className="ml-2">
              ·{" "}
              <span className="tabular-nums font-medium text-foreground">
                {totals.totalItems}
              </span>{" "}
              item{totals.totalItems === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant={groupBy !== "none" ? "default" : "outline"}
                className="h-8"
              >
                <Layers className="h-3.5 w-3.5 mr-1.5" />
                {groupBy === "none" ? "Agrupar" : GROUP_LABEL[groupBy]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[220px]">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide">
                Agrupar por
              </DropdownMenuLabel>
              {(Object.keys(GROUP_LABEL) as GroupKey[]).map((k) => (
                <DropdownMenuItem
                  key={k}
                  onSelect={() => {
                    setGroupBy(k);
                    setCollapsed(new Set());
                  }}
                  className="text-sm"
                >
                  <span className="flex-1">{GROUP_LABEL[k]}</span>
                  {groupBy === k && <Check className="h-3.5 w-3.5 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8">
                <Columns3 className="h-3.5 w-3.5 mr-1.5" />
                Colunas
                <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[10px]">
                  {visible.size}
                </Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[220px]">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide">
                Colunas visíveis
              </DropdownMenuLabel>
              {DEFAULT_COLUMNS.map((k) => (
                <DropdownMenuCheckboxItem
                  key={k}
                  checked={visible.has(k)}
                  onCheckedChange={() => toggle(k)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {COLUMN_META[k].label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <div className="px-2 py-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full h-7 text-xs"
                  onClick={() => {
                    reset();
                    resetWidths();
                  }}
                >
                  Restaurar padrão
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/*
        Tabela — quadro de altura fixa: rolagem dentro, rodapé colado embaixo.

        O quadro inteiro (scroller + rodapé) é que recebe a altura medida. Já
        houve duas versões erradas antes desta: dois `overflow-x-auto`
        aninhados sem altura nenhuma — a barra horizontal ficava no fim de uma
        tabela de 500 linhas — e depois a altura no scroller com o rodapé
        empilhado por fora, o que empurrava a barra horizontal para baixo da
        dobra. Em ambas, rolar para o lado exigia caçar a barra.

        Sticky precisa se ancorar num único scroller, e `border-separate` é
        obrigatório: em `border-collapse` as bordas de elementos sticky não
        pintam. Como isso desliga o `[&_tr]:border-b` do ui/table.tsx, as
        bordas voltam pelas classes abaixo — escopadas aqui, sem afetar as
        outras tabelas do app.

        `table-layout: fixed` é o que dá poder às larguras: sem ele a coluna
        cresce até o conteúdo e nenhum `truncate` chega a valer.
      */}
      <div
        ref={frameRef}
        style={{ height: frameHeight }}
        className="border rounded-lg overflow-hidden flex flex-col"
      >
        <Table
          containerRef={scrollRef}
          containerClassName="flex-1 min-h-0 overflow-auto overscroll-contain"
          onScroll={(e) => setScrolledX(e.currentTarget.scrollLeft > 0)}
          className="border-separate border-spacing-0 [&_tbody_tr>*]:border-b [&_thead_tr>*]:border-b"
          style={{ tableLayout: "fixed", minWidth: totalWidth }}
        >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {visibleCols.map((col, i) => {
                  const meta = COLUMN_META[col];
                  const isPinned = !!meta.pinned && i === 0;
                  const headClass = cn(
                    // z-30 no cruzamento: o canto fixo precisa cobrir tanto as
                    // células do corpo quanto o resto do cabeçalho.
                    "sticky top-0 h-8 bg-muted/95 backdrop-blur-sm",
                    isPinned ? "left-0 z-30" : "z-20",
                    isPinned && scrolledX && "shadow-[4px_0_6px_-4px_rgba(0,0,0,0.18)]",
                  );
                  const handle = (
                    <ColumnResizeHandle
                      label={meta.label}
                      active={resizingKey === col}
                      onStart={(e) => startResize(col, e)}
                      onReset={() => resetWidth(col)}
                    />
                  );
                  if (meta.sortable && meta.sortKey) {
                    return (
                      <Th
                        key={col}
                        label={meta.label}
                        sortKey={meta.sortKey}
                        currentKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align={meta.align ?? "left"}
                        className={headClass}
                        style={{ width: widths[col] }}
                        handle={handle}
                      />
                    );
                  }
                  return (
                    <TableHead
                      key={col}
                      className={headClass}
                      style={{ width: widths[col] }}
                    >
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {meta.label}
                      </span>
                      {handle}
                    </TableHead>
                  );
                })}
                {/*
                  Coluna espaçadora: em `table-layout: fixed` com `width: 100%`,
                  a sobra de uma tela larga seria distribuída entre as colunas e
                  a largura arrastada não seria respeitada. Esta absorve a folga
                  e colapsa para zero quando a tabela estoura.
                */}
                <TableHead
                  aria-hidden
                  className="sticky top-0 z-20 bg-muted/95 p-0 backdrop-blur-sm"
                  style={{ width: "auto" }}
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupBy === "none"
                ? sorted.map((row) => (
                    <DealRow
                      key={row.deal.id}
                      row={row}
                      cols={visibleCols}
                      widths={widths}
                      scrolledX={scrolledX}
                    />
                  ))
                : groups.map((g) => {
                    const summary = summarizeGroup(g.rows);
                    const isCollapsed = collapsed.has(g.groupKey);
                    return (
                      <GroupBlock
                        key={g.groupKey}
                        groupKey={g.groupKey}
                        label={g.label}
                        color={g.color}
                        summary={summary}
                        colSpan={visibleCols.length + 1}
                        collapsed={isCollapsed}
                        onToggle={() => toggleGroup(g.groupKey)}
                        rows={g.rows}
                        cols={visibleCols}
                        widths={widths}
                        scrolledX={scrolledX}
                      />
                    );
                  })}
            </TableBody>
        </Table>

        {/* Rodapé rico com métricas — dentro do quadro medido, sempre visível.
            `shrink-0` porque `index.css` aplica `min-height: 0` em todo `.flex`
            e o rodapé seria espremido pelo scroller. */}
        <div className="border-t bg-muted/20 px-3 py-2 space-y-1 shrink-0">
          {/* Linha 1: contagens por status */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {totals.openCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-blue-500" />
                <span className="text-muted-foreground">Em aberto:</span>
                <span className="font-semibold">{totals.openCount}</span>
                <span className="tabular-nums font-semibold">
                  · {formatBRL(totals.openValue)}
                </span>
              </span>
            )}
            {totals.wonCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">Fechadas:</span>
                <span className="font-semibold text-emerald-600">
                  {totals.wonCount}
                </span>
                <span className="tabular-nums font-semibold text-emerald-600">
                  · {formatBRL(totals.wonValue)}
                </span>
              </span>
            )}
            {totals.lostCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                <span className="text-muted-foreground">Perdidas:</span>
                <span className="font-semibold text-red-600">
                  {totals.lostCount}
                </span>
              </span>
            )}
          </div>

          <Separator />

          {/* Linha 2: métricas agregadas */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <Banknote className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Valor total:</span>
              <span className="tabular-nums font-bold text-foreground">
                {formatBRL(totals.totalValue)}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Ticket médio / negociação:</span>
              <span className="tabular-nums font-bold">
                {formatBRL(totals.avgTicketDeal)}
              </span>
              {totals.wonCount > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  (fechadas: {formatBRL(totals.avgTicketWonDeal)})
                </span>
              )}
            </span>
            {totals.totalItems > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Ticket médio / item:</span>
                <span className="tabular-nums font-bold">
                  {formatBRL(totals.avgTicketItem)}
                </span>
                {totals.wonItems > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    (fechadas: {formatBRL(totals.avgTicketWonItem)})
                  </span>
                )}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Itens:</span>
              <span className="tabular-nums font-bold">
                {totals.totalItems}
              </span>
              {totals.wonItems > 0 && totals.wonItems !== totals.totalItems && (
                <span className="text-[11px] text-muted-foreground">
                  ({totals.wonItems} vendidos)
                </span>
              )}
            </span>
            {totals.wonCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-muted-foreground">Receita fechada:</span>
                <span className="tabular-nums font-bold text-emerald-600">
                  {formatBRL(totals.wonValue)}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
    </DealsEditCtx.Provider>
  );
}

function Th({
  label,
  sortKey,
  currentKey,
  dir,
  onSort,
  align = "left",
  className = "",
  style,
  handle,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: "left" | "right";
  className?: string;
  style?: React.CSSProperties;
  /** Punho de redimensionamento, ancorado na borda direita do cabeçalho. */
  handle?: React.ReactNode;
}) {
  const active = currentKey === sortKey;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead className={className} style={style}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          // `max-w-full` + `truncate`: com largura fixa, um rótulo longo
          // empurraria o ícone de ordenação para debaixo do punho de arraste.
          "inline-flex max-w-full items-center gap-1 text-xs font-semibold uppercase tracking-wide transition hover:text-foreground",
          active ? "text-foreground" : "text-muted-foreground",
          align === "right" && "ml-auto",
        )}
      >
        <span className="truncate">{label}</span>
        <Icon className="h-3 w-3 shrink-0 opacity-70" />
      </button>
      {handle}
    </TableHead>
  );
}

function DealRow({
  row,
  cols,
  widths,
  scrolledX,
}: {
  row: DealsTableRow & { itemsCount: number };
  cols: ColumnKey[];
  widths: Record<ColumnKey, number>;
  scrolledX: boolean;
}) {
  const status = row.deal.status;
  const isWon = status === "won";
  const isLost = status === "lost";
  const typeKey = row.contact?.contactType ?? "empresa";
  const typeCfg = TYPE_CFG[typeKey] ?? TYPE_CFG.empresa;
  const TypeIcon = typeCfg.icon;
  const fullName = [row.contact?.name, row.contact?.lastName]
    .filter(Boolean)
    .join(" ");
  const rawCompany = row.contact?.companyName ?? null;
  const looksLikeCipher =
    rawCompany != null &&
    rawCompany.length > 40 &&
    /^[a-f0-9]+:[a-f0-9]+:[a-f0-9]+/.test(rawCompany);
  const company = looksLikeCipher ? null : rawCompany;
  const location = [row.contact?.city, row.contact?.state]
    .filter(Boolean)
    .join(" · ");

  // Devolve só o CONTEÚDO da célula. A <TableCell> em si é montada por
  // EditableCell, que decide se envolve o conteúdo num editor inline.
  const renderDisplay = (col: ColumnKey) => {
    switch (col) {
      case "title":
        // Uma linha só. O selo "Em aberto" saiu: era o estado da maioria
        // esmagadora das linhas, repetia o que a barra colorida à esquerda já
        // diz e custava uma segunda linha em cada uma delas. Ganho e perda
        // continuam sinalizados — esses são a exceção que vale destacar.
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-1 h-6 rounded-full shrink-0"
              style={{
                backgroundColor: isWon
                  ? "#10b981"
                  : isLost
                  ? "#ef4444"
                  : row.stage?.color ?? "#94a3b8",
              }}
            />
            <CellText title={row.deal.title} className="min-w-0">
              <Link
                href={`/crm/deals/${row.deal.id}`}
                className="text-sm font-semibold hover:text-primary"
              >
                {row.deal.title}
              </Link>
            </CellText>
            {isWon && (
              <Badge className="bg-emerald-600 h-4 shrink-0 text-[9px] px-1">
                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                Fechada
              </Badge>
            )}
            {isLost && (
              <Badge className="bg-red-600 h-4 shrink-0 text-[9px] px-1">
                <XCircle className="h-2.5 w-2.5 mr-0.5" />
                Perdida
              </Badge>
            )}
          </div>
        );
      case "contact":
        return (
          <>
            {row.contact ? (
              <div className="flex items-center gap-1.5">
                <Avatar className="h-6 w-6 shrink-0">
                  <AvatarFallback className="text-[9px] bg-primary/10 text-primary font-semibold">
                    {initials(fullName || company || "?")}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <CellText
                      title={fullName || undefined}
                      className="min-w-0 text-sm font-medium"
                    >
                      {fullName || "Sem contato"}
                    </CellText>
                    <span
                      className={`inline-flex shrink-0 items-center gap-0.5 rounded text-[9px] px-1 py-0 ring-1 font-semibold ${typeCfg.bg} ${typeCfg.color} ${typeCfg.ring}`}
                    >
                      <TypeIcon className="h-2.5 w-2.5" />
                      {typeCfg.short}
                    </span>
                  </div>
                  {(company || location) && (
                    <CellText
                      title={[company, location].filter(Boolean).join(" · ")}
                      className="text-[11px] text-muted-foreground"
                    >
                      {[company, location].filter(Boolean).join(" · ")}
                    </CellText>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </>
        );
      case "stage":
        return (
          <>
            {row.stage ? (
              <div className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: row.stage.color }}
                />
                <CellText title={row.stage.name} className="min-w-0 text-sm">
                  {row.stage.name}
                </CellText>
                {!row.stage.isWon && !row.stage.isLost && (
                  <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                    {row.stage.probability}%
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </>
        );
      case "value":
        return (
          <span className="text-sm font-bold tabular-nums">
            {formatBRL(row.deal.value)}
          </span>
        );
      case "products":
        return (
          <>
            {row.products && row.products.length > 0 ? (
              // Sem `flex-wrap`: numa coluna estreita os chips quebrariam em
              // três linhas e a linha inteira cresceria junto. Cabe um chip; o
              // resto vira "+N", com a lista completa no tooltip.
              <div className="flex items-center gap-1">
                <Package className="h-3 w-3 text-muted-foreground shrink-0" />
                <CellText
                  title={productList(row.products)}
                  className="min-w-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium"
                >
                  {row.products[0].quantity > 1 && (
                    <span className="tabular-nums text-muted-foreground">
                      {row.products[0].quantity}×{" "}
                    </span>
                  )}
                  {row.products[0].name ?? "Produto"}
                </CellText>
                {row.products.length > 1 && (
                  <span
                    className="shrink-0 text-[10px] font-medium text-muted-foreground"
                    title={productList(row.products)}
                  >
                    +{row.products.length - 1}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </>
        );
      case "itemsCount":
        return (
          <>
            {row.itemsCount > 0 ? (
              <span className="inline-flex items-center gap-1 text-sm tabular-nums font-medium">
                <Hash className="h-3 w-3 text-muted-foreground" />
                {row.itemsCount}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </>
        );
      case "owner":
        return (
          <>
            {row.owner?.name ? (
              <div className="flex items-center gap-1.5">
                <Avatar className="h-6 w-6 shrink-0">
                  <AvatarFallback className="text-[9px] bg-primary/10 text-primary font-semibold">
                    {initials(row.owner.name)}
                  </AvatarFallback>
                </Avatar>
                <CellText title={row.owner.name} className="min-w-0 text-xs">
                  {row.owner.name}
                </CellText>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </>
        );
      case "expectedCloseDate":
        return (
          <>
            {row.deal.expectedCloseDate ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      {new Date(row.deal.expectedCloseDate).toLocaleDateString(
                        "pt-BR",
                        { day: "2-digit", month: "short" },
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {new Date(row.deal.expectedCloseDate).toLocaleDateString(
                      "pt-BR",
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </>
        );
      case "updatedAt":
        return (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground">
                  {formatRelative(row.deal.updatedAt)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {new Date(row.deal.updatedAt).toLocaleString("pt-BR")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
    }
  };

  // Fundo da linha. A célula fixa precisa da variante OPACA: com `/30` o
  // conteúdo que rola por baixo apareceria através dela.
  const rowBg = isWon
    ? "bg-emerald-50/30 dark:bg-emerald-950/10"
    : isLost
    ? "bg-red-50/30 dark:bg-red-950/10 opacity-75"
    : "";
  const pinnedBg = isWon
    ? "bg-emerald-50 dark:bg-emerald-950"
    : isLost
    ? "bg-red-50 dark:bg-red-950"
    : "bg-background";

  return (
    <TableRow className={cn("group/row", rowBg)}>
      {cols.map((col, i) => (
        <EditableCell
          key={col}
          col={col}
          index={i}
          row={row}
          width={widths[col]}
          display={renderDisplay(col)}
          pinnedBg={pinnedBg}
          scrolledX={scrolledX}
        />
      ))}
      {/* Par da coluna espaçadora do cabeçalho — absorve a folga da tela. */}
      <TableCell aria-hidden className="p-0" />
    </TableRow>
  );
}

/**
 * Monta a <TableCell> e, quando a coluna declara um editor e o usuário tem
 * permissão, envolve o conteúdo no editor inline correspondente. Sem permissão
 * ou sem editor, renderiza exatamente o que renderizava antes.
 */
function EditableCell({
  col,
  index,
  row,
  width,
  display,
  pinnedBg,
  scrolledX,
}: {
  col: ColumnKey;
  index: number;
  row: DealsTableRow & { itemsCount: number };
  /** Largura atual da coluna, em px — ajustável pelo usuário. */
  width: number;
  display: React.ReactNode;
  pinnedBg: string;
  scrolledX: boolean;
}) {
  const ctx = useContext(DealsEditCtx);
  const meta = COLUMN_META[col];
  const isPinned = !!meta.pinned && index === 0;

  // `overflow-hidden` é o que faz os `truncate` das células valerem: sem ele a
  // célula transborda a largura fixa em vez de cortar.
  const className = cn(
    "py-1.5 overflow-hidden",
    meta.align === "right" && "text-right",
    isPinned && [
      "sticky left-0 z-10",
      pinnedBg,
      "group-hover/row:bg-muted",
      scrolledX && "shadow-[4px_0_6px_-4px_rgba(0,0,0,0.18)]",
    ],
  );

  const editor = meta.editor;
  const canEdit = !!ctx && !!editor && ctx.canEditField(editor.field);

  if (!ctx || !editor || !canEdit) {
    return (
      <TableCell className={className} style={{ width }}>
        {display}
      </TableCell>
    );
  }

  const commit = (next: unknown) =>
    ctx.commit(row.deal.id, editor.field, next, { dealTitle: row.deal.title });

  let content: React.ReactNode = display;

  if (editor.kind === "currency") {
    content = (
      <InlineEditCurrency
        value={row.deal.value}
        onCommit={commit}
        canEdit
        align="right"
        inputClassName="text-sm font-bold"
      >
        {display}
      </InlineEditCurrency>
    );
  } else if (editor.kind === "date") {
    content = (
      <InlineEditDate
        value={row.deal.expectedCloseDate ?? null}
        onCommit={commit}
        canEdit
      >
        {display}
      </InlineEditDate>
    );
  } else if (editor.kind === "stage") {
    content = (
      <InlineEditSelect<number | null>
        value={row.deal.stageId ?? null}
        options={ctx.stages.map((s) => ({
          value: s.id,
          label: s.name,
          node: (
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: s.color ?? "#94a3b8" }}
              />
              <span className="truncate">{s.name}</span>
            </span>
          ),
        }))}
        onCommit={commit}
        canEdit
        fill
        label="estágio"
        emptyMessage="Nenhum estágio neste funil"
      >
        {display}
      </InlineEditSelect>
    );
  } else if (editor.kind === "owner") {
    content = (
      <InlineEditUser
        value={row.deal.ownerUserId ?? null}
        currentUserName={row.owner?.name ?? null}
        onCommit={(next, name) =>
          ctx.commit(row.deal.id, editor.field, next, {
            dealTitle: row.deal.title,
            // Sem o nome, a célula mostraria o responsável anterior até a
            // query voltar — parecendo que a troca não funcionou.
            optimistic: { ownerName: name ?? null },
          })
        }
        canEdit
        fill
      >
        {display}
      </InlineEditUser>
    );
  }

  return (
    <TableCell className={className} style={{ width }}>
      {content}
    </TableCell>
  );
}

function GroupBlock({
  groupKey,
  label,
  color,
  summary,
  colSpan,
  collapsed,
  onToggle,
  rows,
  cols,
  widths,
  scrolledX,
}: {
  groupKey: string;
  label: string;
  color?: string;
  summary: ReturnType<typeof summarizeGroup>;
  colSpan: number;
  collapsed: boolean;
  onToggle: () => void;
  rows: Array<DealsTableRow & { itemsCount: number }>;
  cols: ColumnKey[];
  widths: Record<ColumnKey, number>;
  scrolledX: boolean;
}) {
  return (
    <>
      <TableRow
        className="bg-muted/60 hover:bg-muted/70 border-y cursor-pointer select-none"
        onClick={onToggle}
      >
        <TableCell colSpan={colSpan} className="py-1.5">
          {/* A célula ocupa a largura toda via colSpan, então ao rolar para o
              lado o rótulo do grupo sairia da tela junto com ela. O sticky
              interno mantém o cabeçalho do grupo sempre visível.

              Os totais ficam colados no nome, e não empurrados para a direita
              por um `ml-auto`: alinhados à borda da TABELA, eles sumiam da tela
              exatamente quando a tabela era larga demais — que é quando mais se
              precisa deles. */}
          <div className="sticky left-0 flex w-fit max-w-full items-center gap-2 flex-wrap">
            {collapsed ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            {color && (
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: color }}
              />
            )}
            <span className="text-sm font-semibold">{label}</span>
            <Badge variant="secondary" className="h-4 text-[10px] px-1.5">
              {summary.count}
            </Badge>

            <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1">
                <Banknote className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Total:</span>
                <span className="tabular-nums font-bold">
                  {formatBRL(summary.totalValue)}
                </span>
              </span>
              {summary.totalItems > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Package className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Itens:</span>
                  <span className="tabular-nums font-semibold">
                    {summary.totalItems}
                  </span>
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">T.M. / neg.:</span>
                <span className="tabular-nums font-semibold">
                  {formatBRL(summary.avgTicketDeal)}
                </span>
              </span>
              {summary.totalItems > 0 && (
                <span className="inline-flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">T.M. / item:</span>
                  <span className="tabular-nums font-semibold">
                    {formatBRL(summary.avgTicketItem)}
                  </span>
                </span>
              )}
              {summary.wonCount > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Trophy className="h-3 w-3 text-emerald-600" />
                  <span className="text-muted-foreground">Fechadas:</span>
                  <span className="tabular-nums font-semibold text-emerald-600">
                    {summary.wonCount} · {formatBRL(summary.wonValue)}
                  </span>
                </span>
              )}
            </div>
          </div>
        </TableCell>
      </TableRow>
      {!collapsed &&
        rows.map((row) => (
          <DealRow
            key={`${groupKey}-${row.deal.id}`}
            row={row}
            cols={cols}
            widths={widths}
            scrolledX={scrolledX}
          />
        ))}
    </>
  );
}

/** "2× Consultoria, Treinamento" — legenda completa dos produtos da linha. */
function productList(products: DealsTableRow["products"]): string {
  return (products ?? [])
    .map((p) =>
      p.quantity > 1 ? `${p.quantity}× ${p.name ?? "Produto"}` : p.name ?? "Produto",
    )
    .join(", ");
}

function formatRelative(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  const diffMs = Date.now() - date.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d`;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}
